/**
 * Android bridges for direct control. The web view (origin https://localhost) cannot fetch plain
 * http:// from the switch, so requests to it go through Capacitor's native HTTP; the tablet's own
 * network addresses come from the small NetworkInfo plugin in the Android project.
 */
import { Capacitor, CapacitorHttp, registerPlugin } from '@capacitor/core';
import type { LocalAddress } from './discovery';
import type { HttpTransport } from './switch';

export const nativeHttp: HttpTransport = async ({ url, headers, timeoutMs }) => {
  const response = await CapacitorHttp.request({
    url,
    method: 'GET',
    headers: { ...headers },
    connectTimeout: timeoutMs,
    readTimeout: timeoutMs,
    responseType: 'text',
  });
  const lower: Record<string, string> = {};
  for (const [name, value] of Object.entries(response.headers ?? {})) {
    lower[name.toLowerCase()] = String(value);
  }
  const body: unknown = response.data;
  return {
    status: response.status,
    headers: lower,
    body: typeof body === 'string' ? body : JSON.stringify(body ?? null),
  };
};

interface NetworkInfoPlugin {
  getAddresses(): Promise<{ addresses: LocalAddress[] }>;
}

const NetworkInfo = registerPlugin<NetworkInfoPlugin>('NetworkInfo');

/** The tablet's IPv4 addresses (hotspot, Wi-Fi). Empty off Android. */
export async function localAddresses(): Promise<readonly LocalAddress[]> {
  if (!Capacitor.isNativePlatform()) return [];
  try {
    return (await NetworkInfo.getAddresses()).addresses;
  } catch {
    return [];
  }
}
